import api from '@/api/client/axios';
import { Note, Upload, Ticket } from '@/types';
import { TicketPriority, TicketStatus } from '@/types/enums';
import {
  CompleteTicketDto,
  CreateTicketDto,
  PaginatedResult,
  TicketQueryDto,
  UpdateTicketDto,
} from '@packages/database/dtos';
import { ApiResponse, TicketDetail } from '@packages/database/types';

export interface TicketSubmitPayload {
  priority?: TicketPriority;
  status?: TicketStatus;
  ticketType?: string;
  uploads?: Upload[];
  notes?: Note[];
  ticketId: string;
  crewId?: string | null;
  ticketAssignmentId?: string;
  requiredSkills?: number[];
  customFields?: any;
}

export interface AssignmentPayload {
  isAccepted: boolean;
  ticketId: string | undefined;
  assignmentId: string | null;
}

export interface TicketFilters {
  projectId?: string;
  crewId?: string;
  status?: string;
  priority?: string;
}

export interface CrewTicketsPayload {
  crewId: string;
}

export const createTicket = async (
  payload: CreateTicketDto,
): Promise<TicketDetail> => {
  const response = await api.post<ApiResponse<TicketDetail>>(
    '/tickets',
    payload,
  );
  return response.data.data!;
};

export const createBulkTickets = async (
  payload: any,
): Promise<TicketDetail> => {
  const response = await api.post<ApiResponse<TicketDetail>>(
    '/tickets/bulk',
    payload,
  );
  return response.data.data!;
};

export const getTickets = async (
  filters: TicketQueryDto,
): Promise<PaginatedResult<TicketDetail>> => {
  const cleanedParams = Object.fromEntries(
    Object.entries(filters).filter(
      ([, value]) =>
        value !== '' &&
        value !== 'all' &&
        value !== null &&
        value !== undefined,
    ),
  );

  const response = await api.get<ApiResponse<PaginatedResult<TicketDetail>>>(
    '/tickets',
    { params: cleanedParams },
  );
  return response.data.data!;
};

export const getTicketById = async (
  ticketId: string,
): Promise<TicketDetail> => {
  const response = await api.get<ApiResponse<TicketDetail>>(
    `/tickets/${ticketId}`,
  );
  return response.data.data!;
};

export const updateTicket = async (
  payload: UpdateTicketDto,
): Promise<TicketDetail> => {
  const { ticketId, ...dto } = payload;
  const response = await api.put<ApiResponse<TicketDetail>>(
    `/tickets/${ticketId}`,
    dto,
  );
  return response.data.data!;
};

export const addTicketNote = async (payload: any): Promise<TicketDetail> => {
  const { ticketId, ...dto } = payload;
  const response = await api.put<ApiResponse<TicketDetail>>(
    `/tickets/${ticketId}/note`,
    payload,
  );
  return response.data.data!;
};

export const completeTicket = async (
  dto: CompleteTicketDto,
): Promise<TicketDetail> => {
  const { ticketId, ...payload } = dto;
  const response = await api.post<ApiResponse<TicketDetail>>(
    `/tickets/${ticketId}/complete`,
    payload,
  );
  return response.data.data!;
};

export const updateTicketAssignment = async (
  payload: AssignmentPayload,
): Promise<any> => {
  const response = await api.patch(
    `/tickets/${payload.ticketId}/assignments/${payload.assignmentId}/response`,
    payload,
  );
  return response.data;
};

// Ticket Uploads
export const getTicketUploads = async (ticketId: string): Promise<any> => {
  const response = await api.get(`/tickets/${ticketId}/uploads`);
  return response.data.data;
};

export const approveTicket = async (ticketId: string): Promise<void> => {
  await api.put(`/tickets/${ticketId}/approve`);
};

export const rejectTicket = async (
  ticketId: string,
  rejectionReason: string,
): Promise<void> => {
  await api.put(`/tickets/${ticketId}/reject`, { rejectionReason });
};
